import { createContext } from 'react';
import { getStorage, ref as stRef } from "firebase/storage";
import { initializeApp, getApp, getApps } from "firebase/app";
import { getFunctions, httpsCallable } from 'firebase/functions';
import { getDatabase, ref, query, orderByChild, equalTo } from "firebase/database";
import {
    unlink,
    getAuth,
    OAuthProvider,
    initializeAuth,
    PhoneAuthProvider,
    RecaptchaVerifier,
    updatePhoneNumber,
    GoogleAuthProvider,
    linkWithPhoneNumber,
    signInWithPhoneNumber,
    browserLocalPersistence,
    browserPopupRedirectResolver
} from "firebase/auth";

let getReactNativePersistence;

if (typeof document === "undefined") {
    const { getReactNativePersistence: rnPersistence } = require("firebase/auth");
    getReactNativePersistence = rnPersistence;
}

const FirebaseContext = createContext(null);

let firebase = {
    app: null,
    database: null,
    auth: null,
    storage: null,
};

const createFullStructure = (app, db, auth, storage, config) => {
    const functions = getFunctions(app);

    return {
        app,
        config,
        database: db,
        auth,
        storage,
        functions,
        authRef: getAuth,
        googleProvider: new GoogleAuthProvider(),
        appleProvider: new OAuthProvider('apple.com'),
        phoneProvider: new PhoneAuthProvider(auth),
        RecaptchaVerifier,
        signInWithPhoneNumber,
        updatePhoneNumber,
        unlink,
        googleCredential: (idToken, accessToken) => GoogleAuthProvider.credential(idToken, accessToken),
        linkWithPhoneNumber,
        mobileAuthCredential: (verificationId, code) => PhoneAuthProvider.credential(verificationId, code),
        usersRef: ref(db, 'users'),
        bookingRef: ref(db, 'bookings'),
        cancelreasonRef: ref(db, 'cancel_reason'),
        settingsRef: ref(db, 'settings'),
        smtpRef: ref(db, "smtpdata"),
        carTypesRef: ref(db, 'cartypes'),
        carTypesEditRef: (id) => ref(db, "cartypes/" + id),
        carDocImage: (id) => stRef(storage, `cartypes/${id}`),
        promoRef: ref(db, 'promos'),
        promoEditRef: (id) => ref(db, "promos/" + id),
        notifyRef: ref(db, "notifications/"),
        notifyEditRef: (id) => ref(db, "notifications/" + id),
        addressRef: (uid, id) => ref(db, "savedAddresses/" + uid + "/" + id),
        addressEditRef: (uid) => ref(db, "savedAddresses/" + uid),
        singleUserRef: (uid) => ref(db, "users/" + uid),
        uploadImageTaxi: (uid) => stRef(storage, `taxi/${uid}`),
        profileImageRef: (uid) => stRef(storage, `users/${uid}/profileImage`),
        verifyIdImageRef: (uid) => stRef(storage, `users/${uid}/verifyIdImage`),
        verifyIdImageSelfieRef: (uid) => stRef(storage, `users/${uid}/verifyIdImageSelfie`),
        bookingImageRef: (bookingId, imageType) => stRef(storage, `bookings/${bookingId}/${imageType}`),
        driversRef: query(ref(db, "users"), orderByChild("usertype"), equalTo("driver")),
        driverDocsRef: (uid) => stRef(storage, `users/${uid}/license`),
        driverDocsRefBack: (uid) => stRef(storage, `users/${uid}/licenseBack`),
        singleBookingRef: (bookingKey) => ref(db, "bookings/" + bookingKey),
        requestedDriversRef: (bookingKey) => ref(db, "bookings/" + bookingKey + "/requestedDrivers"),
        referralIdRef: (referralId) => query(ref(db, "users"), orderByChild("referralId"), equalTo(referralId)),
        trackingRef: (bookingId) => ref(db, 'tracking/' + bookingId),
        tasksRef: () => query(ref(db, 'bookings'), orderByChild('status'), equalTo('NEW')),
        singleTaskRef: (uid, bookingId) => ref(db, "bookings/" + bookingId + "/requestedDrivers/" + uid),
        bookingListRef: (uid, role) =>
            role === 'customer' ? query(ref(db, 'bookings'), orderByChild('customer'), equalTo(uid)) :
                role === 'driver' ? query(ref(db, 'bookings'), orderByChild('driver'), equalTo(uid)) :
                    role === 'fleetadmin' ? query(ref(db, 'bookings'), orderByChild('fleetadmin')) :
                        ref(db, 'bookings'),
        chatRef: (bookingId) => ref(db, 'chats/' + bookingId + '/messages'),
        withdrawRef: ref(db, 'withdraws/'),
        languagesRef: ref(db, "languages"),
        languagesEditRef: (id) => ref(db, "languages/" + id),
        walletHistoryRef: (uid) => ref(db, "walletHistory/" + uid),
        userNotificationsRef: (uid) => ref(db, "userNotifications/" + uid),
        userRatingsRef: (uid) => ref(db, "userRatings/" + uid),
        bannersEditRef: (uid) => ref(db, "banners/" + uid),
        bannersAddRef: ref(db, "banners/"),
        bannersRef: ref(db, "banners"),

        bannersImage: (id, index) => stRef(storage, `banners/${id}`),
        carsRef: (uid, role) => role === 'driver' ?
            query(ref(db, 'cars'), orderByChild('driver'), equalTo(uid)) :
            role === 'fleetadmin' ?
                query(ref(db, 'cars'), orderByChild('fleetadmin')) :
                ref(db, 'cars'),
        carAddRef: ref(db, "cars/"),
        carEditRef: (id) => ref(db, "cars/" + id),
        panicAddRef: ref(db, "panics/"),
        panicEditRef: (id) => ref(db, "panics/" + id),
        carImage: (id, index) => stRef(storage, `cars/${id}/${index}`),
        allLocationsRef: ref(db, "locations"),
        userLocationRef: (uid) => ref(db, "locations/" + uid),
        userlocationsCostumerRef: (uid) => ref(db, "locationsCostumer/" + uid),
        callFunction: async (name, data) => {
            if (!functions) throw new Error('Firebase functions not initialized');
            const func = httpsCallable(functions, name);
            const result = await func(data);
            return result.data;
        }
    };
};

const FirebaseProvider = ({ children, AsyncStorage, config }) => {
    let app, auth, database, storage;


    if (!getApps().length) {
        try {
            app = initializeApp(config);

            if (typeof document !== "undefined") {
                auth = initializeAuth(app, {
                    persistence: browserLocalPersistence,
                    popupRedirectResolver: browserPopupRedirectResolver,
                });
            } else {
                auth = initializeAuth(app, {
                    persistence: getReactNativePersistence(AsyncStorage),
                });
            }

            database = getDatabase(app);
            storage = getStorage(app);
        } catch (error) {
            console.errorr("Erro inicializando Firebase: " + error);
        }
    } else {
        app = getApp();
        auth = getAuth(app);
        database = getDatabase(app);
        storage = getStorage(app);
    }

    firebase = createFullStructure(app, database, auth, storage, config);

    return (
        <FirebaseContext.Provider value={firebase}>
            {children}
        </FirebaseContext.Provider>
    );
};

export {
    firebase,
    FirebaseProvider,
    FirebaseContext
};
